import React from 'react';

import { useGetRoles } from '@vlabs/api/artemy/client';
import type { RoleId } from '@vlabs/api/artemy/model';
import { DeleteVariantIcon, InfoCircleIcon } from '@vlabs/icons';
import { Button } from '@vlabs/uikit/components/controls/button/Button';
import { Controller, useForm } from 'react-hook-form';

import { RoleSelect } from '@vlabs/monokit/shared/ui/role-select';

import st from './DeleteRolesForm.module.sass';
import { useDeleteRoleFormTranslation } from './i18n';

export interface DeleteRolesFormValues {
  roleIds: RoleId[];
  newRoleId?: RoleId;
}

export interface DeleteRolesFormProps {
  defaultValues: DeleteRolesFormValues;
  onSubmit: (values: DeleteRolesFormValues) => void | Promise<void>;
  onCancel?: VoidFunction;
}

function DeleteRolesForm({
  defaultValues,
  onSubmit,
  onCancel = undefined,
}: DeleteRolesFormProps): React.JSX.Element {
  const { t } = useDeleteRoleFormTranslation();
  const form = useForm<DeleteRolesFormValues>({
    defaultValues: {
      roleIds: defaultValues.roleIds,
      newRoleId: defaultValues.newRoleId,
    },
  });

  const roleIds = form.watch('roleIds');
  const getRoles = useGetRoles({ role_ids: roleIds }, {
    query: {
      enabled: roleIds.length > 0,
    },
  });
  const { isSubmitting } = form.formState;
  const roles = getRoles.data?.roles ?? [];
  const roleName = roleIds
    .map((roleId) => roles.find((role) => role.role_id === roleId)?.name ?? roleId)
    .join(', ');

  return (
    <form
      className={st.Root}
      onSubmit={form.handleSubmit(onSubmit)}
    >
      <div className={st.TopBlock}>
        <header className={st.Header}>
          <DeleteVariantIcon className={st.Icon} />
          <h5 className={st.Title}>{t('title')}</h5>
        </header>

        <div className={st.Description}>
          {t('description', { roleName })}
        </div>
      </div>

      <div className={st.RoleBlock}>
        <div className={st.Field}>
          <Controller
            control={form.control}
            name="newRoleId"
            render={({ field }) => (
              <RoleSelect
                disabled={isSubmitting}
                excludeRoleIds={roleIds}
                label={t('new_role.label')}
                onChange={field.onChange}
                placeholder={t('new_role.placeholder')}
                value={field.value}
              />
            )}
          />
        </div>

        <div className={st.Hint}>
          <InfoCircleIcon className={st.HintIcon} />
          <span>{t('hint')}</span>
        </div>
      </div>

      <div className={st.Actions}>
        <Button
          className={st.Button}
          disabled={isSubmitting}
          isLoading={isSubmitting}
          kind="negative"
          type="submit"
        >
          {t('delete_button')}
        </Button>

        <Button
          className={st.Button}
          disabled={isSubmitting}
          kind="secondary"
          onClick={onCancel}
          type="button"
          variant="outlined"
        >
          {t('cancel_button')}
        </Button>
      </div>
    </form>
  );
}

export { DeleteRolesForm };
